import { useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { MiLoader } from "@miplatform/components";
import { CONTACT_CARD_EDIT_MODES, CONTACT_TYPES } from "@miplatform/constants";
import { useMiContactCardViewModel } from "@miplatform/view-models";
import { Paper } from "@mui/material";
import { MiContactEdit } from "./MiContactEdit/MiContactEdit";
import { MiContactInfoCard } from "./MiContactInfoCard/MiContactInfoCard";
import { MiContactRelationshipsModal } from "./MiContactRelationshipsModal/MiContactRelationshipsModal";

/**
 * Global contact card implementation.
 *
 * @param {Object} props - The component main prop object.
 * @param {Object} [props.contact] - Object containing the contact information.
 * @param {Array} [props.relationships] - Array of relationship objects associated with the contact.
 * @param {boolean} [props.showMakePrimary] - Shows the make primary action.
 * @param {boolean} [props.isPrimaryDisabled] - Flag to disable the primary contact functionality.
 * @param {boolean} [props.enableDeleteRelationship] - Enables the delete relationship action.
 * @param {boolean} [props.isContactLevelReadOnly] - Flag to make the contact level read-only.
 * @param {boolean} [props.abbreviated=false] - Display the abbreviated version of the component.
 * @param {string} [props.editMode=CONTACT_CARD_EDIT_MODES.NONE] - Set the edit mode for the contact card.
 * @param {Object} [props.referenceIds] - Object containing reference IDs for the contact.
 * @param {string} [props.defaultContactLevel=CONTACT_TYPES.COMMUNITIES] - Default contact level.
 * @param {Array} [props.contactLevelOptions=[]] - Array of contact level options.
 * @param {number} [props.elevation=2] - Set the elevation of the contact card.
 * @param {boolean} [props.fullWidth=true] - Set the card to take full width of its container.
 * @param {Function} [props.onRefresh=() => {}] - Function to handle refreshing the data after a resource updates.
 * @param {Object} [props.containerStyle={}] - Custom inline styles for the card container.
 * @param {Function} [props.onCloseEdit] - Optional function to be executed on the closing of the edit mode.
 * @param {Array} [props.accountIds=null] - Restrict contact creation/search to these account IDs.
 * @param {Array} [props.accountTypes=null] - Restrict contact creation/search to these account types.
 * @param {string} [props.defaultAccountId=null] - Default account ID to associate with a newly created contact.
 * @param {Function} [props.onDeleteRelationshipOverwrite] - Optional override for the delete relationship handler.
 *
 * @returns {JSX.Element} The rendered contact card component.
 *
 * @example
 * <MiContactCard
 *   contact={contactData}
 *   showMakePrimary={true}
 *   relationships={relationshipsData}
 *   isPrimaryDisabled={false}
 *   isContactLevelReadOnly={true}
 *   abbreviated={false}
 *   editMode={CONTACT_CARD_EDIT_MODES.EDIT}
 *   referenceIds={referenceIdsData}
 *   defaultContactLevel={CONTACT_TYPES.COMMUNITIES}
 *   contactLevelOptions={contactLevelOptionsData}
 *   elevation={2}
 *   fullWidth={true}
 *   onRefresh={handleRefresh}
 *   onCloseEdit={handleCloseEdit}
 * />
 */
export const MiContactCard = ({
  contact,
  showMakePrimary,
  relationships,
  isPrimaryDisabled,
  enableDeleteRelationship,
  isContactLevelReadOnly,
  abbreviated = false,
  editMode = CONTACT_CARD_EDIT_MODES.NONE,
  referenceIds,
  defaultContactLevel = CONTACT_TYPES.COMMUNITIES,
  contactLevelOptions = [],
  elevation = 2,
  fullWidth = true,
  onRefresh = () => {},
  containerStyle = {},
  onCloseEdit,
  accountIds = null,
  accountTypes = null,
  defaultAccountId = null,
  onDeleteRelationshipOverwrite,
}) => {
  const { classes } = useStyles();
  const [isEditing, setIsEditing] = useState(!contact);

  const [currentEditMode, setCurrentEditMode] = useState();
  const [isRelationshipsModalOpen, setIsRelationshipModalOpen] =
    useState(false);
  const {
    isLoading,
    contactCategories,
    getContactCategories,
    handleMakePrimary,
    deleteRelationshipOfContact,
  } = useMiContactCardViewModel({
    contact,
    onRefresh,
  });

  const handleRelationshipsModalOpen = () => setIsRelationshipModalOpen(true);

  const handleRelationshipsModalClose = () => setIsRelationshipModalOpen(false);

  const handleCloseEdit = () => {
    setIsEditing(false);
    setCurrentEditMode();
    if (onCloseEdit) {
      onCloseEdit();
    }
  };

  const handleEdit = (mode) => {
    setCurrentEditMode(mode);
    setIsEditing(true);
  };

  useEffect(() => {
    if (
      referenceIds &&
      isEditing &&
      (editMode === CONTACT_CARD_EDIT_MODES.RELATIONSHIP ||
        editMode.includes(CONTACT_CARD_EDIT_MODES.RELATIONSHIP)) &&
      Object.keys(contactCategories).length === 0
    ) {
      getContactCategories(defaultContactLevel);
    }
  }, [
    isRelationshipsModalOpen,
    isEditing,
    contactCategories,
    editMode,
    referenceIds,
  ]);

  return (
    <Paper
      fullWidth={fullWidth}
      elevation={elevation}
      className={`${classes.paper} ${
        abbreviated ? ` ${classes.paperAbbreviated}` : ""
      }`}
      style={containerStyle}
    >
      <MiLoader
        type="linear"
        isLoading={isLoading}
        style={{ position: "absolute", top: 0, left: 0 }}
      />
      {(!isEditing ||
        currentEditMode === CONTACT_CARD_EDIT_MODES.RELATIONSHIP) && (
        <MiContactInfoCard
          isLoading={isLoading}
          showMakePrimary={showMakePrimary}
          enableDeleteRelationship={enableDeleteRelationship}
          contact={contact}
          relationships={relationships}
          abbreviated={abbreviated}
          editMode={editMode}
          onRefresh={onRefresh}
          onEdit={handleEdit}
          onManageRelationships={handleRelationshipsModalOpen}
          onDeleteContact={deleteRelationshipOfContact}
          onMakePrimary={handleMakePrimary}
          onDeleteRelationship={deleteRelationshipOfContact}
          onDeleteRelationshipOverwrite={onDeleteRelationshipOverwrite}
        />
      )}
      {isEditing && (
        <MiContactEdit
          isLoading={isLoading}
          accountIds={accountIds}
          accountTypes={accountTypes}
          defaultAccountId={defaultAccountId}
          contact={contact}
          editMode={editMode}
          isPrimaryDisabled={isPrimaryDisabled}
          referenceIds={referenceIds}
          currentEditMode={currentEditMode}
          contactCategories={contactCategories}
          defaultContactLevel={defaultContactLevel}
          contactLevelOptions={contactLevelOptions}
          onClose={handleCloseEdit}
          onRefresh={onRefresh}
        />
      )}
      <MiContactRelationshipsModal
        isLoading={isLoading}
        open={isRelationshipsModalOpen}
        isPrimaryDisabled={isPrimaryDisabled}
        isContactLevelReadOnly={isContactLevelReadOnly}
        contact={contact}
        relationships={relationships}
        referenceIds={referenceIds}
        contactCategories={contactCategories}
        defaultContactLevel={defaultContactLevel}
        contactLevelOptions={contactLevelOptions}
        onClose={handleRelationshipsModalClose}
        onRefresh={onRefresh}
      />
    </Paper>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    position: "relative",
    borderRadius: theme.spacing(1),
    padding: theme.spacing(2),
  },
  paperAbbreviated: {
    width: "fit-content",
  },
}));
